import { userTypeLabels } from './userLabels';
import type { SaUser } from '@/mocks/saUsers';

interface UserFiltersProps {
  searchTerm: string;
  onSearchChange: (v: string) => void;
  activeUserType: string;
  onUserTypeChange: (v: string) => void;
  activeStatus: string;
  onStatusChange: (v: string) => void;
  activeCountry: string;
  onCountryChange: (v: string) => void;
  activeRadio: string;
  onRadioChange: (v: string) => void;
  dateFrom: string;
  onDateFromChange: (v: string) => void;
  dateTo: string;
  onDateToChange: (v: string) => void;
  userTypeCounts: Record<string, number>;
  statusCounts: Record<string, number>;
  countries: string[];
  radios: string[];
}

export default function UserFilters({
  searchTerm,
  onSearchChange,
  activeUserType,
  onUserTypeChange,
  activeStatus,
  onStatusChange,
  activeCountry,
  onCountryChange,
  activeRadio,
  onRadioChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  userTypeCounts,
  statusCounts,
  countries,
  radios,
}: UserFiltersProps) {
  const hasAdvancedFilters = activeUserType !== 'all' || activeStatus !== 'all' || activeCountry !== 'all' || activeRadio !== 'all' || !!dateFrom || !!dateTo;

  return (
    <div className="space-y-3">
      {/* Search + quick filters row */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por nombre, email, teléfono o radio..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-background-50 border border-background-200 rounded-xl text-foreground-800 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-foreground-400 transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
            >
              <i className="ri-close-line text-foreground-500 text-[10px]"></i>
            </button>
          )}
        </div>

        {/* User type quick chips */}
        <div className="flex items-center gap-1 flex-wrap">
          {Object.entries({ all: userTypeCounts.all || 0, ...userTypeLabels }).filter(([key]) => key !== 'all' || (userTypeCounts.all && userTypeCounts.all > 0)).slice(0, 1).map(([key, label]) => (
            <button
              key={key}
              onClick={() => onUserTypeChange(key)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                activeUserType === key
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-50 border border-background-200 text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {typeof label === 'string' ? label : key}
              {key === 'all' && (
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${activeUserType === 'all' ? 'bg-background-50/20 text-background-50' : 'bg-background-200 text-foreground-500'}`}>{userTypeCounts.all || 0}</span>
              )}
            </button>
          ))}
        </div>

        <button
          onClick={() => {
            onUserTypeChange('all');
            onStatusChange('all');
            onCountryChange('all');
            onRadioChange('all');
            onDateFromChange('');
            onDateToChange('');
            onSearchChange('');
          }}
          className="flex items-center gap-1 px-3 py-2 rounded-xl bg-background-50 border border-background-200 text-foreground-500 text-xs font-medium cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap"
        >
          <i className="ri-filter-off-line text-sm"></i>
          Limpiar filtros
        </button>
      </div>

      {/* Advanced filters row */}
      <div className="flex flex-wrap items-center gap-2">
        {/* User type select */}
        <select
          value={activeUserType}
          onChange={(e) => onUserTypeChange(e.target.value)}
          className="px-3 py-1.5 bg-background-50 border border-background-200 rounded-lg text-foreground-700 text-xs focus:outline-none focus:border-foreground-400 cursor-pointer"
        >
          <option value="all">Todos los tipos ({userTypeCounts.all || 0})</option>
          {Object.entries(userTypeLabels).map(([key, label]) => (
            <option key={key} value={key}>{label} ({userTypeCounts[key] || 0})</option>
          ))}
        </select>

        {/* Status select */}
        <select
          value={activeStatus}
          onChange={(e) => onStatusChange(e.target.value)}
          className="px-3 py-1.5 bg-background-50 border border-background-200 rounded-lg text-foreground-700 text-xs focus:outline-none focus:border-foreground-400 cursor-pointer"
        >
          <option value="all">Todos los estados ({statusCounts.all || 0})</option>
          <option value="activo">Activo ({statusCounts.activo || 0})</option>
          <option value="suspendido">Suspendido ({statusCounts.suspendido || 0})</option>
          <option value="bloqueado">Bloqueado ({statusCounts.bloqueado || 0})</option>
          <option value="pendiente">Pendiente ({statusCounts.pendiente || 0})</option>
        </select>

        {/* Country select */}
        <select
          value={activeCountry}
          onChange={(e) => onCountryChange(e.target.value)}
          className="px-3 py-1.5 bg-background-50 border border-background-200 rounded-lg text-foreground-700 text-xs focus:outline-none focus:border-foreground-400 cursor-pointer"
        >
          <option value="all">Todos los países</option>
          {countries.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        {/* Radio select */}
        <select
          value={activeRadio}
          onChange={(e) => onRadioChange(e.target.value)}
          className="px-3 py-1.5 bg-background-50 border border-background-200 rounded-lg text-foreground-700 text-xs focus:outline-none focus:border-foreground-400 cursor-pointer"
        >
          <option value="all">Todas las radios</option>
          {radios.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>

        {/* Date range */}
        <div className="flex items-center gap-1">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="px-2.5 py-1.5 bg-background-50 border border-background-200 rounded-lg text-foreground-700 text-xs focus:outline-none focus:border-foreground-400 cursor-pointer w-[135px]"
            title="Fecha desde"
          />
          <span className="text-foreground-400 text-xs">—</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            className="px-2.5 py-1.5 bg-background-50 border border-background-200 rounded-lg text-foreground-700 text-xs focus:outline-none focus:border-foreground-400 cursor-pointer w-[135px]"
            title="Fecha hasta"
          />
        </div>
      </div>
    </div>
  );
}